import {
  Facebook,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Mic,
  Globe,
  FileDown,
  Play,
} from "lucide-react";
import { TID } from "@/lib/testIds";
import { API } from "@/lib/api";
import {
  YOUTUBE_URL,
  X_URL,
  X_HANDLE,
  BLUESKY_URL,
  BLUESKY_HANDLE,
  FACEBOOK_URL,
  ATFQ_DOMAIN,
  ATFQ_HOME_URL,
  ATFQ_CONTACT_EMAIL,
} from "@/components/atfq/constants";

const BADGE =
  "https://customer-assets.emergentagent.com/job_aviation-integrity/artifacts/emcpu9jb_generated-image-2026-01-18-16-16-51.1280pix.no%20background.png";

const FooterBrand = () => (
    <div className="md:col-span-5">
      <div className="flex items-center gap-4">
        <img
          src={BADGE}
          alt="ATFQ · Answer The F**king Question — Vic Appiah watchdog podcast badge"
          className="h-28 md:h-32 w-auto drop-shadow-lg"
        />
        <div>
          <div className="font-display text-lg tracking-tight text-white leading-none">
            VICTOR <span className="text-[var(--accent)]">APPIAH</span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-1">
            Independent · Host of ATFQ
          </div>
        </div>
      </div>
      <p className="mt-5 text-sm text-white/50 leading-relaxed max-w-sm">
        Aviation-grade rigor. Independent voice. A watchdog podcast
        built on radical transparency and unyielding accountability —
        broadcasting from Georgia to a national audience.
      </p>
    </div>
);

const FooterContact = () => (
    <div className="md:col-span-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-4">
        Contact
      </div>
      <ul className="space-y-3 text-sm text-white/70">
        <li className="flex items-center gap-3">
          <Mail className="w-4 h-4 text-[var(--accent)]" />
          <a
            href={`mailto:${ATFQ_CONTACT_EMAIL}`}
            className="hover:text-white"
          >
            {ATFQ_CONTACT_EMAIL}
          </a>
        </li>
        <li className="flex items-center gap-3">
          <Phone className="w-4 h-4 text-[var(--accent)]" />
          <a href="tel:+19133461888" className="hover:text-white">
            (913) 346-1888
          </a>
        </li>
        <li className="flex items-start gap-3">
          <MapPin className="w-4 h-4 text-[var(--accent)] mt-0.5" />
          <span>Henry County, GA</span>
        </li>
      </ul>

      {/* ATFQ Podcast contact */}
      <div className="mt-8 pt-6 border-t border-white/10">
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2">
          <Mic className="w-3 h-3 text-[var(--accent)]" />
          ATFQ Podcast
        </div>
        <ul className="space-y-3 text-sm text-white/70">
          <li className="flex items-center gap-3">
            <Globe className="w-4 h-4 text-[var(--accent)]" />
            <a
              href={ATFQ_HOME_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-atfq-domain"
              className="hover:text-white"
            >
              {ATFQ_DOMAIN}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-[var(--accent)]" />
            <a
              href={`mailto:${ATFQ_CONTACT_EMAIL}`}
              data-testid="footer-atfq-email"
              className="hover:text-white"
            >
              {ATFQ_CONTACT_EMAIL}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <FileDown className="w-4 h-4 text-[var(--accent)]" />
            <a
              href={`${API}/atfq/media-kit.pdf`}
              data-testid="footer-atfq-media-kit"
              className="hover:text-white"
              aria-label="Download ATFQ media kit PDF"
            >
              Media Kit (PDF)
            </a>
          </li>
        </ul>
      </div>
    </div>
);

const FooterFollow = () => (
    <div className="md:col-span-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-4">
        Follow ATFQ
      </div>
      <a
        href={YOUTUBE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="footer-youtube-cta"
        className="group flex items-center justify-between gap-4 border border-[var(--accent)] bg-[var(--accent)]/10 hover:bg-[var(--accent)] p-4 mb-5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Play className="w-4 h-4 fill-current text-[var(--accent)] group-hover:text-white transition-colors" />
          <div>
            <div className="text-sm font-medium text-white leading-tight">
              Follow ATFQ on YouTube
            </div>
            <div className="text-[11px] text-white/50 group-hover:text-white/80 transition-colors">
              New episodes · @ATFQ-pcast
            </div>
          </div>
        </div>
        <Youtube className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
      </a>
      <div className="flex flex-wrap gap-3">
        <SocialLink
          href={YOUTUBE_URL}
          Icon={Youtube}
          label="YouTube"
          testid="footer-social-youtube"
        />
        <SocialLink href={X_URL} Icon={XIcon} label={`X · ${X_HANDLE}`} testid="footer-social-x" />
        <SocialLink href={BLUESKY_URL} Icon={BlueskyIcon} label={`Bluesky · ${BLUESKY_HANDLE}`} testid="footer-social-bluesky" />
        <SocialLink href={FACEBOOK_URL} Icon={Facebook} label="Facebook · ATFQ" testid={TID.socialFacebook} />
      </div>

      <nav className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-widest text-white/60">
        <a href="/#atfq" className="hover:text-white">Podcast</a>
        <a href="/voices" className="hover:text-white">Voices</a>
        <a href="/#investigations" className="hover:text-white">Investigations</a>
        <a href="/#about" className="hover:text-white">The Host</a>
        <a href="/#survey" className="hover:text-white">Sit Down</a>
      </nav>
    </div>
);

export const Footer = () => {
  return (
    <footer
      id="contact"
      data-testid={TID.footerSection}
      className="relative bg-[var(--navy-deep)] text-white"
    >
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />

      {/* Lower footer */}
      <div className="relative px-6 md:px-12 lg:px-20 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <FooterBrand />
          <FooterContact />
          <FooterFollow />
        </div>

        <div className="mt-14 border-t border-white/10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div
            data-testid={TID.footerDisclosure}
            className="font-mono text-[11px] uppercase tracking-widest text-white/50"
          >
            Editorially independent · Views expressed are the host&apos;s own.
          </div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-white/30">
            © {new Date().getFullYear()} · ATFQ · Henry County, GA
          </div>
        </div>
      </div>
    </footer>
  );
};

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.9l-5.4-7.06L4 22H.75l8.02-9.17L.5 2h7.05l4.88 6.45L18.244 2Zm-1.2 18h1.8L7.05 3.9H5.1L17.04 20Z" />
  </svg>
);

const BlueskyIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M5.2 3.6C7.9 5.6 10.8 9.7 12 11.9c1.2-2.2 4.1-6.3 6.8-8.3 1.9-1.4 5.2-2.6 5.2 1 0 .7-.4 6-.7 6.9-.8 3-3.8 3.8-6.5 3.3 4.7.8 5.9 3.5 3.3 6.1-4.9 5-7-1.3-8-2.9l-.1-.3-.1.3c-1 1.6-3.1 7.9-8 2.9-2.6-2.6-1.4-5.3 3.3-6.1-2.7.5-5.7-.3-6.5-3.3C.4 10.6 0 5.3 0 4.6c0-3.6 3.3-2.4 5.2-1Z" />
  </svg>
);

const SocialLink = ({ href, Icon, label, testid }) => (
  <a
    href={href}
    data-testid={testid}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="w-11 h-11 border border-white/15 hover:border-[var(--accent)] hover:bg-white/5 flex items-center justify-center transition-colors group"
  >
    <Icon className="w-4 h-4 text-white/70 group-hover:text-[var(--accent)] transition-colors" />
  </a>
);

export default Footer;
